import React, { useEffect, useRef, useState } from "react";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { Home, MessageCircle, Zap, GraduationCap, Tv as TvIcon, Gamepad2, Lock, Unlock, ShoppingBag, Radio, Calendar } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useKidMode } from "../context/KidModeContext";

/**
 * KidsShell — the walled-garden layout for the entire Kids Network.
 *
 * Visible on /kids and all /kids/* routes. Replaces the main Header
 * completely with:
 *   · Kids logo → /kids (never links back to the main site)
 *   · 6 kid-only nav icons (Home · Community · Reels · Tutoring · Games · Kids TV)
 *   · No "Sign in" / "Subscribe" / "More" dropdown — no adult pathways
 * Plus an age gate that asks if the visitor is under 18 on first visit.
 */
export default function KidsShell({ children }) {
  const { pathname } = useLocation();
  const nav = useNavigate();
  const { user } = useAuth();
  const km = useKidMode();
  const [ageOpen, setAgeOpen] = useState(false);
  const [lockChallenge, setLockChallenge] = useState(null); // {q, answer, target}
  const [lockAnswer, setLockAnswer] = useState("");

  // Show the age gate once per session unless Kid Mode is already locked
  // in place by a parent.
  useEffect(() => {
    try {
      const key = "tapin_kids_age_check_v1";
      const done = sessionStorage.getItem(key);
      if (!done && !km.active) setAgeOpen(true);
    } catch {}
  }, [km.active]);

  // Kid Mode Lock Screen — catches ANY click that would navigate out of
  // /kids/* or /tv/kids while Kid Mode is active, and presents a tiny
  // grown-up challenge (math problem). Answering wrong keeps the kid
  // inside. Knowing the PIN and opening /parent lets a grown-up unlock
  // the whole experience.
  useEffect(() => {
    if (!km.active) return;
    const onClick = (e) => {
      const a = e.target.closest?.("a[href]");
      if (!a) return;
      const href = a.getAttribute("href") || "";
      if (!href || href.startsWith("#") || href.startsWith("javascript:")) return;
      // Allow all /kids/* + /tv/kids* (internal kids area)
      const allow = href.startsWith("/kids") || href.startsWith("/tv/kids") || href === "/kids";
      if (allow) return;
      // External (http/mailto/tel) or main-site route → challenge
      e.preventDefault(); e.stopPropagation();
      const a1 = 2 + Math.floor(Math.random() * 10);
      const a2 = 3 + Math.floor(Math.random() * 10);
      setLockChallenge({ q: `What is ${a1} + ${a2}?`, answer: a1 + a2, target: href });
      setLockAnswer("");
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [km.active]);

  const markDone = () => {
    try { sessionStorage.setItem("tapin_kids_age_check_v1", "1"); } catch {}
    setAgeOpen(false);
  };

  const submitLock = () => {
    if (Number(lockAnswer) === lockChallenge.answer) {
      const target = lockChallenge.target;
      setLockChallenge(null); setLockAnswer("");
      if (target.startsWith("/")) nav(target);
      else window.location.href = target;
    } else {
      setLockAnswer(""); setLockChallenge({ ...lockChallenge, wrong: true });
    }
  };

  const NAV = [
    { to: "/kids",           icon: Home,          label: "Home",      testid: "knav-home" },
    { to: "/kids/community", icon: MessageCircle, label: "Community", testid: "knav-community" },
    { to: "/kids/reels",     icon: Zap,           label: "Reels",     testid: "knav-reels" },
    { to: "/kids/tutoring",  icon: GraduationCap, label: "Tutoring",  testid: "knav-tutoring" },
    { to: "/kids/games",     icon: Gamepad2,      label: "Games",     testid: "knav-games" },
    { to: "/kids/radio",     icon: Radio,         label: "Radio",     testid: "knav-radio" },
    { to: "/kids/shop",      icon: ShoppingBag,   label: "Shop",      testid: "knav-shop" },
    { to: "/kids/guide",     icon: Calendar,      label: "TV Guide",  testid: "knav-guide" },
    { to: "/tv/kids",        icon: TvIcon,        label: "Kids TV",   testid: "knav-tv" },
  ];

  return (
    <div data-testid="kids-shell" className="min-h-screen bg-gradient-to-br from-[#0a0420] via-[#150a35] to-[#1a0737] text-white">
      {/* ── Kids-only header ────────────────────────────────────────── */}
      <header
        data-testid="kids-header"
        className="fixed top-0 left-0 right-0 z-[60] backdrop-blur-xl bg-black/50 border-b-4"
        style={{ borderImage: "linear-gradient(90deg,#ec4899,#f59e0b,#22d3ee,#8b5cf6) 1" }}
      >
        <div className="max-w-7xl mx-auto flex items-center gap-2 sm:gap-3 px-3 sm:px-6 py-2">
          <Link to="/kids" data-testid="kids-header-logo" className="shrink-0">
            <img src="/kids-learn-play-logo.png" alt="Tap IN Learn and Play" className="h-12 sm:h-14 w-auto drop-shadow-2xl" />
          </Link>
          <nav data-testid="kids-nav" className="flex-1 flex items-center gap-1 sm:gap-2 overflow-x-auto" style={{ scrollbarWidth: "none" }}>
            {NAV.map((n) => {
              const Icon = n.icon;
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/kids"}
                  data-testid={n.testid}
                  className={({ isActive }) =>
                    `shrink-0 inline-flex items-center gap-1.5 font-black text-xs sm:text-sm px-2.5 sm:px-4 py-2 rounded-full transition ${isActive ? "bg-gradient-to-r from-pink-500 to-fuchsia-500 text-white shadow-lg scale-[1.02]" : "text-white/70 hover:text-white hover:bg-white/10"}`
                  }
                >
                  <Icon className="w-4 h-4" /> <span className="hidden sm:inline">{n.label}</span>
                </NavLink>
              );
            })}
          </nav>
          {user && (
            <div className="hidden sm:flex items-center gap-2 shrink-0">
              <div className="text-xs text-white/60 max-w-[120px] truncate">Hi, {user.name?.split(" ")[0]}!</div>
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-400 to-fuchsia-500 flex items-center justify-center text-lg shadow-lg">{user.avatar_emoji || user.picture ? "⭐" : "⭐"}</div>
            </div>
          )}
        </div>
      </header>

      {/* Main content — pushed below the header */}
      <main className="pt-[64px] sm:pt-[72px]">{children}</main>

      {/* Kids footer — also walled-garden, no outbound links */}
      <footer data-testid="kids-footer" className="mt-12 border-t border-white/10 bg-black/30 py-5 text-center">
        <img src="/kids-learn-play-logo.png" alt="" className="inline-block h-10 w-auto opacity-80" />
        <div className="text-white/50 text-xs mt-2">Tap IN Kids Network — Safe · Fun · Educational</div>
        <div className="text-white/30 text-[10px] mt-1">All content kid-approved &amp; parent-managed.</div>
      </footer>

      {/* Kid Mode Lock Screen — only fires when Kid Mode is active AND
          the user clicks an outbound link. Lets grown-ups answer a small
          math challenge to unlock navigation. */}
      {lockChallenge && (
        <div data-testid="kid-mode-lock-overlay" className="fixed inset-0 z-[110] flex items-center justify-center bg-black/90 backdrop-blur-md p-4">
          <div className="bg-gradient-to-br from-amber-400 via-pink-500 to-violet-600 rounded-3xl p-6 md:p-8 max-w-sm w-full ring-4 ring-white/30 shadow-2xl text-center">
            <Lock className="w-8 h-8 text-white mx-auto mb-2" />
            <h2 className="font-display font-black text-2xl text-white">Grown-up check!</h2>
            <p className="text-white/85 text-sm mt-1">Only grown-ups can leave the Kids area.</p>
            <div className="mt-5 font-display font-black text-4xl text-white">{lockChallenge.q}</div>
            <input
              data-testid="kid-lock-answer"
              value={lockAnswer}
              type="number"
              inputMode="numeric"
              onChange={(e) => setLockAnswer(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submitLock(); }}
              className="mt-3 w-24 bg-white/95 text-[#222] font-black text-2xl text-center rounded-xl px-2 py-2 outline-none"
              autoFocus
            />
            {lockChallenge.wrong && <div className="mt-2 text-white/95 text-sm font-bold">Nope, try again!</div>}
            <div className="mt-4 flex items-center gap-2 justify-center">
              <button data-testid="kid-lock-submit" onClick={submitLock} className="bg-white text-[#222] font-black px-5 py-2 rounded-full inline-flex items-center gap-1"><Unlock className="w-4 h-4" /> Unlock</button>
              <button data-testid="kid-lock-cancel" onClick={() => { setLockChallenge(null); setLockAnswer(""); }} className="bg-white/20 text-white font-bold px-5 py-2 rounded-full">Stay here</button>
            </div>
            {/* Parent escape hatch — reset Kid Mode entirely with the
                master PIN "2424". Recovers from a forgotten PIN. */}
            <div className="mt-5 pt-4 border-t border-white/20 text-left">
              <div className="text-white/90 text-xs font-bold mb-1">Parent? Enter PIN 2424 to turn off Kid Mode:</div>
              <div className="flex gap-2">
                <input
                  data-testid="kid-lock-pin-reset"
                  type="password"
                  inputMode="numeric"
                  maxLength={8}
                  placeholder="PIN"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      const res = km.exitKidMode(e.currentTarget.value);
                      if (res?.ok) {
                        setLockChallenge(null); setLockAnswer("");
                        // Leave the walled garden entirely — go to the
                        // main Tap IN home so grown-ups see the change.
                        nav("/", { replace: true });
                      } else {
                        alert("Wrong PIN. Try 2424.");
                      }
                    }
                  }}
                  className="flex-1 bg-white/20 text-white placeholder-white/60 rounded-lg px-3 py-1.5 text-sm outline-none"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Age-gate modal */}
      {ageOpen && (
        <div data-testid="age-gate-overlay" className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-md p-4">
          <div className="bg-gradient-to-br from-pink-500 via-fuchsia-500 to-sky-500 rounded-3xl p-6 md:p-8 max-w-md w-full ring-4 ring-white/30 shadow-2xl">
            <div className="text-center">
              <img src="/kids-learn-play-logo.png" alt="" className="mx-auto h-20 w-auto drop-shadow-2xl" />
              <h2 className="font-display font-black text-3xl md:text-4xl text-white mt-3 tracking-tight drop-shadow-lg">How old are you?</h2>
              <p className="text-white/90 text-sm mt-1">This helps us keep you safe. Pick the one that's true!</p>
            </div>
            <div className="mt-6 grid grid-cols-1 gap-3">
              <button
                data-testid="age-under-18"
                onClick={() => {
                  markDone();
                  // Lock parent account: turn on Kid Mode so the whole
                  // app clamps to kid-safe. We set a human-readable
                  // default PIN so a grown-up can always unlock. The
                  // master reset PIN "2424" also works anywhere Kid
                  // Mode is in effect.
                  if (!km.active) {
                    try { km.enterKidMode("2424"); } catch {}
                  }
                  try { alert("Kid Mode is ON! 🧸\n\nGrown-up PIN: 2424\n(A parent can also set a new PIN from /parent dashboard.)"); } catch {}
                }}
                className="bg-white/95 hover:bg-white text-[#222] font-black text-xl px-5 py-4 rounded-2xl shadow-xl transition hover:scale-[1.02]"
              >
                I'm under 18 <span className="text-2xl ml-1">🧒</span>
              </button>
              <button
                data-testid="age-18-or-older"
                onClick={markDone}
                className="bg-white/15 hover:bg-white/25 text-white font-bold text-base px-5 py-3 rounded-2xl backdrop-blur border border-white/25"
              >
                I'm 18 or older (parent / grown-up)
              </button>
            </div>
            <div className="mt-4 flex items-center gap-1 text-white/70 text-[11px] justify-center">
              <Lock className="w-3 h-3" /> Parents can unlock Kid Mode any time from /parent.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
